/**
 * SignupPage.js — RC 1.20A
 *
 * Required fields: first name, last name, email, password, confirm
 * password, CAPTCHA. Optional: nickname.
 *
 * Password security: min 8 chars, one upper, one lower, one digit, one
 * special. Live checklist renders pass/fail per rule as the user types.
 *
 * Email confirmation is intentionally NOT relied upon — the CTA button
 * is labelled "Create account & enter" and the Supabase project is
 * expected to have "Confirm email" turned OFF. If confirmation is still
 * enabled, the existing "Check your email" screen still works and we
 * degrade gracefully.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle2, XCircle, Mail, Briefcase, User } from 'lucide-react';
import PasswordInput from '@/components/PasswordInput';
import BrandLogo from '@/components/BrandLogo';
import Captcha from '@/components/Captcha';
import LegalFooter from '@/components/LegalFooter';
import AuthBrandPanel from '@/components/AuthBrandPanel';
import { track } from '@/lib/analytics';

// ── Password rule matrix ──────────────────────────────────────────────
const PASSWORD_RULES = [
  { key: 'length', label: 'At least 8 characters',         test: (p) => p.length >= 8 },
  { key: 'upper',  label: 'One uppercase letter',          test: (p) => /[A-Z]/.test(p) },
  { key: 'lower',  label: 'One lowercase letter',          test: (p) => /[a-z]/.test(p) },
  { key: 'digit',  label: 'One number',                    test: (p) => /\d/.test(p) },
  { key: 'symbol', label: 'One special character',         test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function SignupPage() {
  const { signUp, resendConfirmation } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [nickname,  setNickname]  = useState('');
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [captchaOk, setCaptchaOk] = useState(false);
  const [role, setRole]         = useState('candidate'); // 'candidate' only — employer signup gated until launch
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [confirmationSent, setConfirmationSent] = useState(false);
  const [resendStatus, setResendStatus]         = useState('');

  const ruleState = useMemo(
    () => PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(password) })),
    [password],
  );
  const allRulesOk  = ruleState.every((r) => r.ok);
  const matchOk     = password.length > 0 && password === confirmPassword;
  const identityOk  = firstName.trim() && lastName.trim() && email.trim();
  // RC 1.35.F3 follow-up — employer signups are gated until launch.
  // The role state is pre-seeded to 'candidate' and the employer card
  // is rendered as a non-clickable "Coming soon" placeholder, so this
  // gate effectively reduces to "the user must keep the candidate
  // selection". The check is preserved for future re-enablement.
  const employerSignupsEnabled =
    String(process.env.REACT_APP_EMPLOYER_SIGNUPS_ENABLED || 'false').toLowerCase() === 'true';
  const roleOk      = role === 'candidate' || (employerSignupsEnabled && role === 'employer');
  const canSubmit   = !loading && identityOk && allRulesOk && matchOk && captchaOk && roleOk;

  // Ordered list of gates — the first unmet one is surfaced below the
  // disabled CTA so the user knows exactly what to fix.
  const blockingReason = (() => {
    if (loading) return '';
    if (!firstName.trim() || !lastName.trim()) return 'Enter your first and last name.';
    if (!email.trim())  return 'Enter your email address.';
    if (!allRulesOk)    return 'Password must satisfy every rule below.';
    if (!matchOk)       return 'Confirm-password must match your password.';
    if (!captchaOk)     return 'Complete the human check.';
    if (!roleOk)        return 'Choose whether you are signing up as a Candidate or an Employer.';
    return '';
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!identityOk) { setError('First name, last name, and email are required.'); return; }
    if (!allRulesOk) { setError('Password does not satisfy all requirements.');     return; }
    if (!matchOk)    { setError('Passwords do not match.');                         return; }
    if (!captchaOk)  { setError('Please complete the CAPTCHA.');                    return; }
    if (!roleOk)     { setError('Please choose Candidate or Employer.');            return; }

    setLoading(true);
    track('signup_started', { role });
    const { data, error: authError } = await signUp(
      email.trim(), password,
      {
        first_name: firstName.trim(),
        last_name:  lastName.trim(),
        nickname:   nickname.trim() || null,
        role,
      },
    );

    if (authError) {
      setError(authError.message);
      setLoading(false);
    } else if (data?.session) {
      // Email-confirm disabled → user is live. PublicRoute redirects.
      track('signup_completed', { role, confirmation_required: false });
    } else {
      setConfirmationSent(true);
      setLoading(false);
      track('signup_completed', { role, confirmation_required: true });
    }
  };

  const handleResend = async () => {
    setResendStatus('');
    const { error: resendError } = await resendConfirmation(email.trim());
    setResendStatus(resendError ? resendError.message : 'Confirmation email resent.');
  };

  // Confirmation-sent screen (only rendered if Supabase project still
  // requires email confirmation).
  if (confirmationSent) {
    return (
      <div className="flex min-h-screen bg-ndor-canvas" data-testid="signup-confirmation">
        <div className="flex flex-1 flex-col justify-center px-6 py-12 lg:px-16 bg-ndor-surface">
          <div className="mx-auto w-full max-w-sm text-center">
            <div className="mb-8 flex justify-center"><BrandLogo variant="symbol" size={56} /></div>
            <Mail className="h-12 w-12 mx-auto text-ndor-gold mb-4" />
            <h1 className="text-2xl font-serif-display font-semibold tracking-tight text-ndor-ink">Check your email</h1>
            <p className="text-sm text-ndor-ink-secondary mt-3 leading-relaxed">
              We sent a confirmation link to <strong className="text-ndor-ink">{email}</strong>.
              Click it, then sign in.
            </p>
            <Button type="button" onClick={handleResend} variant="outline" className="mt-6 border-ndor-edge bg-ndor-surface-alt" data-testid="resend-button">
              Resend confirmation email
            </Button>
            {resendStatus && (
              <p className="mt-3 text-xs text-ndor-ink-secondary" data-testid="resend-status">{resendStatus}</p>
            )}
            <p className="mt-8 text-sm text-ndor-ink-secondary">
              <Link to="/login" className="text-ndor-info hover:text-ndor-primary-hover">← Back to login</Link>
            </p>
          </div>
        </div>
        <div className="hidden lg:block lg:flex-1 relative brand-hex-grid">
          <div className="absolute inset-0 flex items-center justify-center p-12">
            <BrandLogo variant="symbol" size={96} className="brand-symbol-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-ndor-canvas" data-testid="signup-page">
      <div className="flex flex-1">
      {/* Left — Premium brand hero (desktop only) */}
      <div className="hidden lg:flex lg:flex-1 relative brand-hex-grid">
        <AuthBrandPanel variant="desktop" />
      </div>

      {/* Right — Signup form */}
      <div className="flex flex-1 min-w-0 flex-col justify-center px-6 py-10 sm:py-12 lg:px-16 bg-ndor-surface">
        <div className="mx-auto w-full max-w-md sm:max-w-lg lg:max-w-md">
          {/* RC 1.35.F3 follow-up — full mobile/tablet brand hero. */}
          <AuthBrandPanel variant="mobile" />
          {/* Desktop compact brand for the right column */}
          <div className="hidden lg:flex mb-12 items-center gap-3">
            <BrandLogo variant="symbol" size={44} />
          </div>

          <h1 className="text-3xl sm:text-4xl font-serif-display font-semibold tracking-tight text-ndor-ink">
            Create your account
          </h1>
          <p className="text-sm sm:text-[15px] text-ndor-ink-secondary mt-2">
            Candidates and employers. One workspace.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="signup-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="first_name" className="text-ndor-ink">First name</Label>
                <Input
                  id="first_name"
                  data-testid="first-name-input"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="bg-ndor-surface-alt border-ndor-edge text-ndor-ink"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="last_name" className="text-ndor-ink">Last name</Label>
                <Input
                  id="last_name"
                  data-testid="last-name-input"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  className="bg-ndor-surface-alt border-ndor-edge text-ndor-ink"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="nickname" className="text-ndor-ink">
                Nickname <span className="text-ndor-ink-muted font-normal">(optional)</span>
              </Label>
              <Input
                id="nickname"
                data-testid="nickname-input"
                autoComplete="nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="What should we call you?"
                className="bg-ndor-surface-alt border-ndor-edge text-ndor-ink"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-ndor-ink">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-ndor-surface-alt border-ndor-edge text-ndor-ink"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-ndor-ink">Password</Label>
              <PasswordInput
                id="password"
                data-testid="password-input"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              {/* Live checklist */}
              <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1" data-testid="password-checklist">
                {ruleState.map((r) => (
                  <li
                    key={r.key}
                    data-testid={`rule-${r.key}-${r.ok ? 'pass' : 'fail'}`}
                    className={`flex items-center gap-1.5 text-xs ${r.ok ? 'text-ndor-success' : 'text-ndor-ink-muted'}`}
                  >
                    {r.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                    {r.label}
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm_password" className="text-ndor-ink">Confirm password</Label>
              <PasswordInput
                id="confirm_password"
                data-testid="confirm-password-input"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              {confirmPassword.length > 0 && !matchOk && (
                <p className="text-xs text-ndor-error" data-testid="confirm-password-mismatch">
                  Passwords do not match.
                </p>
              )}
              {matchOk && (
                <p className="text-xs text-ndor-success flex items-center gap-1.5" data-testid="confirm-password-match">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Passwords match.
                </p>
              )}
            </div>

            <Captcha onChange={setCaptchaOk} testId="signup-captcha" />

            {/* Role selector — binary. Required. Stored on the
                profile row via the self-access endpoint in
                AuthContext.signUp. */}
            <div className="space-y-2" data-testid="signup-role-selector">
              <Label className="text-ndor-ink">I'm signing up as</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setRole('candidate')}
                  data-testid="signup-role-candidate"
                  aria-pressed={role === 'candidate'}
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    role === 'candidate'
                      ? 'border-ndor-gold bg-ndor-surface-alt ring-1 ring-ndor-gold/50'
                      : 'border-ndor-edge hover:border-ndor-ink-muted'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-ndor-ink-secondary" />
                    <p className="text-sm font-medium text-ndor-ink">Candidate</p>
                  </div>
                  <p className="text-xs text-ndor-ink-muted mt-1 leading-snug">
                    Track roles, analyse fit, tailor applications.
                  </p>
                </button>
                <button
                  type="button"
                  onClick={employerSignupsEnabled ? () => setRole('employer') : undefined}
                  disabled={!employerSignupsEnabled}
                  data-testid="signup-role-employer"
                  aria-disabled={!employerSignupsEnabled}
                  aria-pressed={employerSignupsEnabled && role === 'employer'}
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    !employerSignupsEnabled
                      ? 'border-ndor-edge bg-ndor-surface-alt/30 cursor-not-allowed opacity-70'
                      : role === 'employer'
                        ? 'border-ndor-gold bg-ndor-surface-alt ring-1 ring-ndor-gold/50'
                        : 'border-ndor-edge hover:border-ndor-ink-muted'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-ndor-ink-muted" />
                    <p className="text-sm font-medium text-ndor-ink">Employer</p>
                    {!employerSignupsEnabled && (
                      <span
                        className="ml-auto text-[9px] uppercase tracking-[0.16em] font-mono-ui px-1.5 py-0.5 rounded border border-ndor-gold/40 text-ndor-gold bg-ndor-gold/5"
                        data-testid="signup-role-employer-coming-soon"
                      >
                        Coming soon
                      </span>
                    )}
                  </div>
                  {employerSignupsEnabled ? (
                    <p className="text-xs text-ndor-ink-muted mt-1 leading-snug">
                      Post roles, screen evidence, rank applicants.
                    </p>
                  ) : (
                    <p
                      className="text-xs text-ndor-ink-muted mt-1 leading-snug"
                      data-testid="signup-role-employer-waitlist-copy"
                    >
                      Employer workspace — coming soon. We're opening
                      employer access after candidate launch. Contact{' '}
                      <a
                        href="mailto:support@ndor.app?subject=NDORFlow%20-%20Employer%20waitlist"
                        className="text-ndor-info hover:text-ndor-primary-hover underline"
                        data-testid="signup-role-employer-waitlist-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        support@ndor.app
                      </a>{' '}
                      and mention NDORFlow to join the waitlist.
                    </p>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-ndor-error bg-ndor-error-light border border-ndor-error-border rounded-md px-3 py-2" data-testid="signup-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              className="w-full bg-ndor-primary hover:bg-ndor-primary-hover text-ndor-primary-foreground"
              disabled={!canSubmit}
              data-testid="signup-submit"
            >
              {loading ? (
                <span className="inline-flex items-center"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating account…</span>
              ) : 'Create account & enter'}
            </Button>

            {/* Helper line — appears only when the button is
                disabled and tells the user the first unmet gate. */}
            {!canSubmit && blockingReason && (
              <p
                className="text-center text-xs text-ndor-ink-muted -mt-2"
                data-testid="signup-submit-helper"
              >
                {blockingReason}
              </p>
            )}

            <p className="text-center text-sm text-ndor-ink-secondary">
              Already have an account?{' '}
              <Link to="/login" className="text-ndor-info hover:text-ndor-primary-hover" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </div>
      </div>
      <LegalFooter variant="auth" />
    </div>
  );
}
